// OrgGraphFilters — segmented control + conditional department dropdown
// that drives <OrgGraph> in US6. This component is purely presentational;
// it owns no graph data and emits all state changes through callbacks.
//
// Layout
//   [ All people ] [ My team ] [ By department ]  ( <select …> )
//
// `currentUserUpn` is used only to enable/disable the "My team" pill; the
// actual filtering happens in useGraphFilter.
//
// See specs/entra-org-chart/us5-filters.md.
import { cn } from '@/lib/utils';
import type { IOrgGraphDepartment } from '@skystone/shared';
import type { FilterParams } from './hooks/useGraphFilter';

export type FilterMode = FilterParams['mode'];

export interface OrgGraphFiltersProps {
  mode: FilterMode;
  departments: IOrgGraphDepartment[];
  /** UPN of the JWT-authenticated viewer, or null when they have no Entra row. */
  currentUserUpn: string | null;
  /** Selected department slug when mode === 'department'. */
  selectedDepartmentId: string | null;
  onModeChange: (mode: FilterMode) => void;
  onDepartmentChange: (departmentId: string) => void;
  className?: string;
}

interface PillDef {
  mode: FilterMode;
  label: string;
}

const PILLS: readonly PillDef[] = [
  { mode: 'all', label: 'All people' },
  { mode: 'my-team', label: 'My team' },
  { mode: 'department', label: 'By department' },
];

const PILL_BASE =
  'inline-flex h-9 items-center justify-center rounded-full px-4 text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2';

const PILL_ACTIVE = 'bg-primary text-primary-foreground';
const PILL_INACTIVE =
  'border border-input text-muted-foreground hover:bg-accent hover:text-accent-foreground';
const PILL_DISABLED = 'opacity-50 cursor-not-allowed';

function sortByName(a: IOrgGraphDepartment, b: IOrgGraphDepartment): number {
  return a.name.localeCompare(b.name);
}

export function OrgGraphFilters({
  mode,
  departments,
  currentUserUpn,
  selectedDepartmentId,
  onModeChange,
  onDepartmentChange,
  className,
}: Readonly<OrgGraphFiltersProps>): JSX.Element {
  const hasCurrentUser = currentUserUpn !== null;
  const sortedDepartments = [...departments].sort(sortByName);
  // When the parent passes mode='my-team' but no current user is known, the
  // hook falls back to the full graph. Mirror that in the UI by treating
  // 'all' as the effective active filter so the segmented control always has
  // exactly one pill highlighted and aria-pressed reflects what is actually
  // shown on screen.
  const displayMode: FilterMode =
    mode === 'my-team' && !hasCurrentUser ? 'all' : mode;

  return (
    <div
      className={cn('flex flex-wrap items-center gap-2', className)}
      data-testid="org-graph-filters"
    >
      <fieldset className="inline-flex flex-wrap items-center gap-2" aria-label="Graph filter">
        {PILLS.map((pill) => {
          const isMyTeam = pill.mode === 'my-team';
          const disabled = isMyTeam && !hasCurrentUser;
          // Active state is derived from `displayMode`, not `mode`, so the
          // fallback-to-all case marks 'All' active while 'My team' remains
          // disabled. A disabled pill is never active.
          const isActive = pill.mode === displayMode && !disabled;
          return (
            <button
              key={pill.mode}
              type="button"
              aria-pressed={isActive}
              disabled={disabled}
              title={
                disabled
                  ? 'Current user not found in Entra directory'
                  : undefined
              }
              data-testid={`org-graph-filter-pill-${pill.mode}`}
              onClick={() => onModeChange(pill.mode)}
              className={cn(
                PILL_BASE,
                isActive ? PILL_ACTIVE : PILL_INACTIVE,
                disabled && PILL_DISABLED,
              )}
            >
              {pill.label}
            </button>
          );
        })}
      </fieldset>

      {mode === 'department' && (
        <label className="inline-flex items-center gap-2 text-sm text-muted-foreground">
          <span className="sr-only">Department</span>
          <select
            data-testid="org-graph-department-select"
            value={selectedDepartmentId ?? ''}
            onChange={(event) => onDepartmentChange(event.target.value)}
            className={cn(
              'h-9 min-w-[12rem] rounded-md border border-input bg-background px-3 text-sm',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
            )}
          >
            <option value="" disabled>
              Select a department
            </option>
            {sortedDepartments.map((department) => (
              <option key={department.id} value={department.id}>
                {department.name} ({department.memberCount} people)
              </option>
            ))}
          </select>
        </label>
      )}
    </div>
  );
}
